import dayjs from "dayjs";
import { dedupePathLabReportRecords, getBoundPathLabReportId, getLabTestCatalogId, getPathLabReportId, getPathLabReportResultValue, normalizePathLabReportFields, resolvePathLabReportDate, resolvePathLabReportDateKey } from "../../utils/labTestDedupeUtils";
import { getAllLabTests, getLabtestsList } from "../../services/labtestService";
import {
  getMergedPathLabReportsForPatient,
  getPathPabReportByPatient,
  mergePathLabReportPreferFilled,
} from "../../services/pathLabTestReportService";
import {
  getPrescriptionsByPatientId,
  getPrescriptionsWithLabTest,
  getOpdLabTests,
} from "../../services/prescriptionService";
import { PREDEFINED_TEST_CATEGORIES, PREDEFINED_TEST_NAMES } from "./predefinedTestCategories";
import { getLinkedAppointmentId, getPrescriptionRecordId } from "../../utils/patientTimelineVisitUtils";

export const normalizeTestName = (value) => (value || "").toString().trim().toLowerCase();

export const getLabTestName = (labTest) =>
  (labTest?.testName ||
    labTest?.test_name ||
    labTest?.name ||
    labTest?.labTestName ||
    "")
    .toString()
    .trim();

const getReportTestName = (report) =>
  normalizeTestName(
    report?.testName || report?.test_name || report?.labTestName || report?.name
  );

const reportPatientIdsMatch = (report, labTestOrPrescription) => {
  const left =
    report?.patientId ?? report?.patient_id ?? null;
  const right =
    labTestOrPrescription?.patientId ??
    labTestOrPrescription?.patient_id ??
    null;
  if (left == null || right == null) return true;
  return Number(left) === Number(right);
};

export const reportMatchesLabTest = (report, labTest) => {
  if (!report || !labTest) return false;
  if (!reportPatientIdsMatch(report, labTest)) return false;

  const reportCatalogId = getLabTestCatalogId(report);
  const labCatalogId = getLabTestCatalogId(labTest);
  const labTestName = normalizeTestName(getLabTestName(labTest));
  const reportName = getReportTestName(report);

  // Matching catalog ids — still reject when both sides have conflicting test names
  // (stale/wrong labTestId on a report must not bind CBC ↔ Thyroid).
  if (reportCatalogId != null && labCatalogId != null && reportCatalogId === labCatalogId) {
    if (labTestName && reportName && labTestName !== reportName) return false;
    return true;
  }

  // Catalog ids disagree or missing — fall through to test name (same patient already checked).
  if (labTestName && reportName) {
    return labTestName === reportName;
  }

  const reportId = getPathLabReportId(report);
  const labReportId = getBoundPathLabReportId(labTest);
  if (reportId != null && labReportId != null && reportId === labReportId) {
    if (
      reportCatalogId != null &&
      labCatalogId != null &&
      reportCatalogId !== labCatalogId
    ) {
      return false;
    }
    if (labTestName && reportName && labTestName !== reportName) return false;
    return true;
  }

  return false;
};

export const getOpdLabTestStatus = (labTest) => {
  const status = labTest?.resultStatus;
  if (status == null || status === "") return "PENDING";
  return String(status).toUpperCase();
};

export const getIpdLabTestStatus = (labTest) => {
  const status = labTest?.testStatus;
  if (status == null || status === "") return "PENDING";
  return String(status).toUpperCase();
};

/** Sample collection IDs configured on a catalog lab test. */
export const extractLabTestSampleIds = (catalogLabTest) => {
  if (!catalogLabTest) return [];
  const ids = [];
  if (Array.isArray(catalogLabTest.sampleEntries) && catalogLabTest.sampleEntries.length) {
    for (const entry of catalogLabTest.sampleEntries) {
      const value = entry?.sample_id ?? entry?.sampleId;
      if (value != null && value !== "") ids.push(Number(value));
    }
  }
  if (catalogLabTest.sampleId) {
    String(catalogLabTest.sampleId)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => ids.push(Number(part)));
  }
  return [...new Set(ids.filter((id) => !Number.isNaN(id)))];
};

export const findCatalogLabTest = (patientOrIpdTest, catalogTests = []) => {
  const list = Array.isArray(catalogTests) ? catalogTests : [];
  const catalogId = getLabTestCatalogId(patientOrIpdTest);
  if (catalogId != null) {
    const byId = list.find((labTest) => Number(labTest.id) === Number(catalogId));
    if (byId) return byId;
  }
  const name = normalizeTestName(
    patientOrIpdTest?.resolvedTestName ||
      patientOrIpdTest?.testName ||
      patientOrIpdTest?.test_name ||
      patientOrIpdTest?.name ||
      patientOrIpdTest?.labTestName
  );
  if (!name) return null;
  return (
    list.find(
      (labTest) =>
        normalizeTestName(
          labTest.testName || labTest.name || labTest.test_name || labTest.labTestName
        ) === name
    ) || null
  );
};

/** True when this lab test is linked to at least one of the checked sample types. */
export const isLabTestCoveredBySelectedSamples = (
  patientOrIpdTest,
  catalogTests,
  selectedSampleIds
) => {
  const selected = new Set(
    (Array.isArray(selectedSampleIds) ? selectedSampleIds : [])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
  );
  if (selected.size === 0) return false;
  const required = extractLabTestSampleIds(
    findCatalogLabTest(patientOrIpdTest, catalogTests)
  );
  if (required.length === 0) return false;
  return required.some((id) => selected.has(id));
};

export const filterTestsBySelectedSamples = (tests, catalogTests, selectedSampleIds) =>
  (Array.isArray(tests) ? tests : []).filter((test) =>
    isLabTestCoveredBySelectedSamples(test, catalogTests, selectedSampleIds)
  );

export const isOpdResultEditable = (labTest) => {
  const status = getOpdLabTestStatus(labTest);
  return status === "COLLECTED" || status === "COMPLETE";
};

export const isIpdResultEditable = (test) => {
  const status = getIpdLabTestStatus(test);
  return status === "COLLECTED" || status === "COMPLETED" || status === "COMPLETE";
};

export const hasSubCategories = (testName) => {
  if (!testName) return false;
  const normalized = normalizeTestName(testName);
  return PREDEFINED_TEST_NAMES.some((name) => normalizeTestName(name) === normalized);
};

export const getPredefinedTestKey = (testName) =>
  PREDEFINED_TEST_NAMES.find((name) => normalizeTestName(name) === normalizeTestName(testName));

export const getSubCategoriesForTest = (testName) => {
  const key = getPredefinedTestKey(testName);
  return key ? PREDEFINED_TEST_CATEGORIES[key] : [];
};

export const parseSubCategoryResults = (result) => {
  if (!result) return null;
  try {
    const parsed = typeof result === "string" ? JSON.parse(result) : result;
    if (parsed.subCategoryResults) return parsed;

    const subResultsString = parsed.s || parsed.subResults;
    if (subResultsString) {
      const subCategoryResults = {};
      subResultsString.split("|").forEach((item) => {
        const [key, value] = item.split(":");
        if (key && value !== undefined) {
          subCategoryResults[key.trim()] = value.trim();
        }
      });
      return {
        subCategoryResults,
        remarks: parsed.r || parsed.remarks || "",
      };
    }
  } catch (_) {
    return null;
  }
  return null;
};

/**
 * Parse stored lab result for entry/edit forms.
 * Supports compact subcategory payloads, JSON `{ result }`, and plain strings.
 */
export const parseResultForEntryForm = (result) => {
  const subcategoryParsed = parseSubCategoryResults(result);
  if (subcategoryParsed?.subCategoryResults) {
    return {
      result: "",
      remarks: subcategoryParsed.remarks || "",
      subCategoryResults: subcategoryParsed.subCategoryResults,
    };
  }

  if (result == null || result === "") {
    return { result: "", remarks: "", subCategoryResults: {} };
  }

  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) {
      return { result: "", remarks: "", subCategoryResults: {} };
    }
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return { result: trimmed, remarks: "", subCategoryResults: {} };
    }
    try {
      const obj = JSON.parse(trimmed);
      if (obj && typeof obj === "object") {
        if (obj.result != null && String(obj.result).trim() !== "") {
          return {
            result: String(obj.result),
            remarks: obj.remarks || obj.r || "",
            subCategoryResults: {},
          };
        }
        if (obj.subCategoryResults || obj.s || obj.subResults) {
          const parsed = parseSubCategoryResults(obj);
          if (parsed?.subCategoryResults) {
            return {
              result: "",
              remarks: parsed.remarks || "",
              subCategoryResults: parsed.subCategoryResults,
            };
          }
        }
      }
    } catch (_) {
      return { result: trimmed, remarks: "", subCategoryResults: {} };
    }
  }

  if (typeof result === "object") {
    if (result.result != null && String(result.result).trim() !== "") {
      return {
        result: String(result.result),
        remarks: result.remarks || result.r || "",
        subCategoryResults: {},
      };
    }
  }

  return { result: "", remarks: "", subCategoryResults: {} };
};

export const formatIpdTestDateForApi = (testDate) => {
  if (!testDate) return dayjs().format("YYYY-MM-DDTHH:mm:ss");
  if (Array.isArray(testDate)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = testDate;
    return dayjs(new Date(year, month - 1, day, hour, minute, second)).format("YYYY-MM-DDTHH:mm:ss");
  }
  return dayjs(testDate).format("YYYY-MM-DDTHH:mm:ss");
};

export const buildIpdResultUpdatePayload = (test, testResult) => ({
  id: test.id,
  patientId: test.patientId,
  admissionId: test.admissionId,
  assignedBy: test.assignedBy,
  testId: test.testId || test.labTestId,
  labId: test.labId,
  testResult,
  testStatus: "COMPLETED",
  testDate: formatIpdTestDateForApi(test.testDate),
});

/** Keep only sub-category keys that belong to the test's predefined template. */
export const filterSubCategoryResultsForTest = (testName, subCategoryResults = {}) => {
  const predefined = getSubCategoriesForTest(testName);
  if (!predefined.length) return subCategoryResults;

  const allowed = new Set(predefined.map((sub) => sub.name));
  return Object.fromEntries(
    Object.entries(subCategoryResults).filter(([key]) => allowed.has(key))
  );
};

export const buildCompactResultPayload = (subCategoryResults, remarks, testName) => {
  const filtered = testName
    ? filterSubCategoryResultsForTest(testName, subCategoryResults)
    : subCategoryResults;

  const subResultsString = Object.entries(filtered || {})
    .filter(([, value]) => value && value.trim() !== "")
    .map(([key, value]) => `${key}:${value}`)
    .join("|");

  const compactResult = { s: subResultsString };
  if (remarks && remarks.trim()) {
    compactResult.r = remarks.trim();
  }
  return JSON.stringify(compactResult);
};

/** Load lab test catalog maps keyed by id and normalized name. */
let catalogMapsCache = null;
let catalogMapsCacheTime = 0;
const CATALOG_MAPS_CACHE_TTL_MS = 5 * 60 * 1000;

/** Parse sub-categories from a lab-test catalog row (custom or org-edited predefined). */
export const normalizeCatalogSubCategories = (catalogTest) => {
  if (!catalogTest) return [];

  let raw = null;
  if (Array.isArray(catalogTest.subCategories) && catalogTest.subCategories.length > 0) {
    raw = catalogTest.subCategories;
  } else if (
    Array.isArray(catalogTest.labTestSubCategories) &&
    catalogTest.labTestSubCategories.length > 0
  ) {
    raw = catalogTest.labTestSubCategories;
  } else if (
    Array.isArray(catalogTest.lab_test_sub_categories) &&
    catalogTest.lab_test_sub_categories.length > 0
  ) {
    raw = catalogTest.lab_test_sub_categories;
  } else if (Array.isArray(catalogTest.sub_categories) && catalogTest.sub_categories.length > 0) {
    raw = catalogTest.sub_categories;
  } else if (typeof catalogTest.subCategories === "string") {
    try {
      const parsed = JSON.parse(catalogTest.subCategories);
      if (Array.isArray(parsed) && parsed.length > 0) raw = parsed;
    } catch (_) {
      raw = null;
    }
  }

  if (!raw) return [];

  return raw
    .map((sub) => ({
      name: (sub?.name || sub?.subCategoryName || "").toString().trim(),
      referenceValue: sub?.referenceValue || sub?.reference_value || "",
      unit: sub?.unit || "",
      category: sub?.category || null,
    }))
    .filter((sub) => sub.name);
};

const mergeCatalogEntries = (existing, incoming) => {
  if (!existing) return incoming;
  if (!incoming) return existing;
  const existingSubs = normalizeCatalogSubCategories(existing);
  const incomingSubs = normalizeCatalogSubCategories(incoming);
  return {
    ...existing,
    ...incoming,
    subCategories:
      incomingSubs.length > 0
        ? incoming.subCategories ?? incomingSubs
        : existing.subCategories ?? existingSubs,
    referenceValue:
      incoming.referenceValue ||
      incoming.reference_value ||
      existing.referenceValue ||
      existing.reference_value ||
      null,
    normalRange:
      incoming.normalRange ||
      incoming.normal_range ||
      existing.normalRange ||
      existing.normal_range ||
      null,
  };
};

export const getLabTestCatalogMaps = async () => {
  const now = Date.now();
  if (catalogMapsCache && now - catalogMapsCacheTime < CATALOG_MAPS_CACHE_TTL_MS) {
    return catalogMapsCache;
  }

  const catalogById = new Map();
  const catalogByName = new Map();

  const upsertCatalogTest = (test) => {
    if (!test) return;

    if (test.id != null) {
      const id = Number(test.id);
      catalogById.set(id, mergeCatalogEntries(catalogById.get(id), test));
    }

    const normalized = normalizeTestName(
      test.testName || test.name || test.test_name || test.labTestName
    );
    if (normalized) {
      catalogByName.set(normalized, mergeCatalogEntries(catalogByName.get(normalized), test));
    }
  };

  try {
    const [orgList, baseList] = await Promise.all([
      getLabtestsList().catch((error) => {
        console.warn("[labTestWorkflowUtils] Org lab tests list failed:", error);
        return [];
      }),
      getAllLabTests().catch((error) => {
        console.warn("[labTestWorkflowUtils] Base lab tests list failed:", error);
        return [];
      }),
    ]);

    (Array.isArray(orgList) ? orgList : []).forEach(upsertCatalogTest);
    // Base table is the source of truth for custom/org-edited subCategories + refs
    (Array.isArray(baseList) ? baseList : []).forEach(upsertCatalogTest);
  } catch (error) {
    console.warn("[labTestWorkflowUtils] Could not load lab test catalog:", error);
  }

  catalogMapsCache = { catalogById, catalogByName };
  catalogMapsCacheTime = Date.now();
  return catalogMapsCache;
};

/** Resolve catalog row for a report/test by id or name. */
export const resolveCatalogTestForReport = (
  reportOrTest,
  catalogById = null,
  catalogByName = null
) => {
  if (!reportOrTest) return null;

  const catalogId = getLabTestCatalogId(reportOrTest);
  if (catalogId != null && isCatalogMap(catalogById) && catalogById.has(Number(catalogId))) {
    return catalogById.get(Number(catalogId));
  }

  const normalized = normalizeTestName(
    getLabTestName(reportOrTest) ||
      reportOrTest.testName ||
      reportOrTest.test_name ||
      reportOrTest.name ||
      reportOrTest.labTestName
  );
  if (normalized && isCatalogMap(catalogByName) && catalogByName.has(normalized)) {
    return catalogByName.get(normalized);
  }

  return null;
};

/**
 * Sub-categories for display/entry: prefer org catalog (custom + edited refs),
 * fall back to hardcoded predefined templates.
 */
export const resolveDisplaySubCategories = (
  testName,
  catalogTest = null,
  options = {}
) => {
  const fromCatalog = normalizeCatalogSubCategories(catalogTest);
  if (fromCatalog.length > 0) return fromCatalog;

  if (options.preferPredefined !== false && hasSubCategories(testName)) {
    return getSubCategoriesForTest(testName);
  }

  return [];
};

/** Compact name for fuzzy matching (e.g. "Blood Glucose (PP)" ↔ "Blood Glucose PP"). */
const compactTestName = (value) =>
  normalizeTestName(value).replace(/[^a-z0-9]/g, "");

const REF_NAME_ALIASES = {
  triglycerided: "triglycerides",
  cholestrol: "cholesterol",
  "glucose-f": "blood glucose (fasting)",
  "glucose f": "blood glucose (fasting)",
  glucosef: "blood glucose (fasting)",
  "blood glucose random": "blood glucose (random)",
  "blood glucose pp": "blood glucose (pp)",
  "hiv-1/2": "hiv i by card",
  "hiv 1/2": "hiv i by card",
  hiv12: "hiv i by card",
  "ra factor": "ra factor",
  "pregnancy test": "pregnancy test",
};

const namesLooselyMatch = (a, b) => {
  const na = normalizeTestName(a);
  const nb = normalizeTestName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const ca = compactTestName(a);
  const cb = compactTestName(b);
  return Boolean(ca && cb && ca === cb);
};

const findInSubCategoryList = (subs, testName) => {
  if (!Array.isArray(subs) || !subs.length || !testName) return null;
  const direct = subs.find((sub) => namesLooselyMatch(sub.name, testName));
  if (direct) return direct;

  const aliasTarget =
    REF_NAME_ALIASES[normalizeTestName(testName)] ||
    REF_NAME_ALIASES[compactTestName(testName)];
  if (aliasTarget) {
    return subs.find((sub) => namesLooselyMatch(sub.name, aliasTarget)) || null;
  }
  return null;
};

/** Find a matching subcategory definition across all predefined parent tests. */
export const findPredefinedSubCategoryByName = (testName) => {
  if (!testName) return null;
  for (const subs of Object.values(PREDEFINED_TEST_CATEGORIES)) {
    const match = findInSubCategoryList(subs, testName);
    if (match) return match;
  }
  return null;
};

/**
 * Resolve Ref. Value (+ unit) for a lab test report row.
 * Order: catalog top-level → catalog subcategory → report fields →
 * predefined subcategory (for standalone parameter tests like HDL, TSH).
 */
export const resolveLabTestReferenceMeta = ({
  testName,
  catalogTest = null,
  report = null,
  subCategories = null,
} = {}) => {
  const pick = (referenceValue, unit = "") => ({
    referenceValue: referenceValue || "",
    unit: unit || "",
  });

  const topLevel =
    catalogTest?.referenceValue ||
    catalogTest?.reference_value ||
    catalogTest?.normalRange ||
    catalogTest?.normal_range ||
    null;
  if (topLevel) {
    return pick(topLevel, catalogTest?.unit || "");
  }

  const catalogSubs =
    Array.isArray(subCategories) && subCategories.length > 0
      ? subCategories
      : normalizeCatalogSubCategories(catalogTest);

  if (catalogSubs.length > 0) {
    const matched = findInSubCategoryList(catalogSubs, testName);
    if (matched?.referenceValue || matched?.unit) {
      return pick(matched.referenceValue, matched.unit);
    }
    if (catalogSubs.length === 1) {
      return pick(catalogSubs[0].referenceValue, catalogSubs[0].unit);
    }
    const withRef = catalogSubs.find((sub) => sub.referenceValue);
    if (withRef) return pick(withRef.referenceValue, withRef.unit);
  }

  const fromReport =
    report?.referenceValue ||
    report?.reference_value ||
    report?.normalRange ||
    report?.normal_range ||
    null;
  if (fromReport) {
    return pick(fromReport, report?.unit || "");
  }

  const predefinedSub = findPredefinedSubCategoryByName(testName);
  if (predefinedSub) {
    return pick(predefinedSub.referenceValue, predefinedSub.unit);
  }

  return pick("", "");
};

export const resolveReportTestNameFromCatalog = (report, catalogById, catalogByName) => {
  const catalogId = getLabTestCatalogId(report);
  if (catalogId != null && isCatalogMap(catalogById) && catalogById.has(catalogId)) {
    const catalogTest = catalogById.get(catalogId);
    return (
      catalogTest.testName ||
      catalogTest.name ||
      catalogTest.test_name ||
      catalogTest.labTestName ||
      getLabTestName(report)
    );
  }

  const normalized = normalizeTestName(getLabTestName(report));
  if (normalized && isCatalogMap(catalogByName) && catalogByName.has(normalized)) {
    const catalogTest = catalogByName.get(normalized);
    return (
      catalogTest.testName ||
      catalogTest.name ||
      catalogTest.test_name ||
      catalogTest.labTestName ||
      getLabTestName(report)
    );
  }

  return getLabTestName(report) || report?.testName || report?.test_name || "";
};

const isCatalogMap = (value) => value instanceof Map;

/** Resolve canonical lab test id/name from catalog.
 * Prefer an explicit test name when present; fall back to catalog id.
 * Never let a blank name prevent predefined templates (Typhoid IgG/IgM, etc.).
 */
export const resolveLabTestIdentity = (labTest, catalogByName = null, catalogById = null) => {
  if (!labTest) return labTest;

  const prescriptionName = getLabTestName(labTest);
  const normalized = normalizeTestName(prescriptionName);

  // Explicit name wins when it maps to a catalog entry (fixes stale id e.g. Typhoid with id 44).
  if (normalized && isCatalogMap(catalogByName) && catalogByName.has(normalized)) {
    const catalogTest = catalogByName.get(normalized);
    const catalogId = Number(catalogTest.id);
    const canonicalName =
      catalogTest.testName ||
      catalogTest.name ||
      catalogTest.test_name ||
      catalogTest.labTestName ||
      prescriptionName;
    return {
      ...labTest,
      testName: canonicalName || prescriptionName,
      labTestId: catalogId,
      testId: catalogId,
    };
  }

  const rowCatalogId = getLabTestCatalogId(labTest);
  if (rowCatalogId != null && isCatalogMap(catalogById) && catalogById.has(rowCatalogId)) {
    const catalogTest = catalogById.get(rowCatalogId);
    const canonicalName =
      catalogTest.testName ||
      catalogTest.name ||
      catalogTest.test_name ||
      catalogTest.labTestName ||
      prescriptionName;
    return {
      ...labTest,
      testName: canonicalName || prescriptionName,
      labTestId: rowCatalogId,
      testId: rowCatalogId,
    };
  }

  return {
    ...labTest,
    testName: prescriptionName,
    labTestId: rowCatalogId ?? labTest.labTestId ?? labTest.testId ?? null,
    testId: rowCatalogId ?? labTest.testId ?? labTest.labTestId ?? null,
  };
};

/** Prefer prescription identity; if name is blank, use matching report name/id. */
export const resolveAuthoritativeCatalogId = (
  labTest,
  report,
  catalogByName = null,
  catalogById = null
) => {
  const resolved = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  const fromLabTest = getLabTestCatalogId(resolved);
  const labName = getLabTestName(resolved);

  // If prescription row has a usable name or id, keep it.
  if (labName && fromLabTest != null) return fromLabTest;
  if (labName && fromLabTest == null) {
    const normalized = normalizeTestName(labName);
    if (isCatalogMap(catalogByName) && catalogByName.has(normalized)) {
      return Number(catalogByName.get(normalized).id);
    }
  }
  if (fromLabTest != null) return fromLabTest;

  if (report) {
    const reportName = normalizeTestName(getLabTestName(report));
    if (isCatalogMap(catalogByName) && reportName && catalogByName.has(reportName)) {
      return Number(catalogByName.get(reportName).id);
    }
    return getLabTestCatalogId(report);
  }

  return null;
};

/** Resolve template name for form/display. Prefer matching report name when prescription name is blank. */
export const resolveCanonicalTestNameForEntry = (
  labTest,
  report,
  catalogById = null,
  catalogByName = null
) => {
  const resolvedLabTest = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  let prescriptionName = getLabTestName(resolvedLabTest);

  // Orphan/virtual rows sometimes lack testName — use linked report name so Typhoid template resolves.
  if (!prescriptionName && report) {
    prescriptionName = getLabTestName(report) || report?.testName || report?.test_name || "";
  }

  const catalogId = resolveAuthoritativeCatalogId(
    { ...resolvedLabTest, testName: prescriptionName || resolvedLabTest.testName },
    report,
    catalogByName,
    catalogById
  );

  if (catalogId != null && isCatalogMap(catalogById) && catalogById.has(catalogId)) {
    const catalogTest = catalogById.get(catalogId);
    return (
      catalogTest?.testName ||
      catalogTest?.name ||
      catalogTest?.test_name ||
      catalogTest?.labTestName ||
      prescriptionName
    );
  }

  // Predefined template names (Typhoid, Urine Test, etc.) must win when they match ignore-case.
  if (prescriptionName && hasSubCategories(prescriptionName)) {
    return getPredefinedTestKey(prescriptionName) || prescriptionName;
  }

  return prescriptionName;
};

/** Report id for submit — only when report row matches this lab test (catalog/name + patientId). */
export const resolveSubmitReportId = (
  labTest,
  report,
  reports = [],
  prescription = null,
  catalogByName = null,
  catalogById = null,
  preferredReportId = null
) => {
  const resolvedLabTest = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  const scopedLabTest = {
    ...resolvedLabTest,
    patientId:
      resolvedLabTest?.patientId ??
      labTest?.patientId ??
      prescription?.patientId ??
      null,
  };
  const reportList = (Array.isArray(reports) ? reports : []).filter((row) =>
    reportPatientIdsMatch(row, scopedLabTest)
  );

  const findByReportId = (targetId) => {
    if (targetId == null || targetId === "") return null;
    const targetNum = Number(targetId);
    if (
      report &&
      (getPathLabReportId(report) === targetNum ||
        getPathLabReportId(report) === targetId)
    ) {
      return report;
    }
    return (
      reportList.find((row) => {
        const id = getPathLabReportId(row);
        return id === targetNum || id === targetId || Number(id) === targetNum;
      }) || null
    );
  };

  const accept = (row) => {
    if (!row || !reportMatchesLabTest(row, scopedLabTest)) return null;
    const id = getPathLabReportId(row);
    return id != null ? id : null;
  };

  // 1) Table-row focus reportId (authoritative when identity matches).
  if (preferredReportId != null && preferredReportId !== "") {
    const preferred = accept(findByReportId(preferredReportId));
    if (preferred != null) return preferred;
  }

  // 2) Explicit reportId on the lab-test row — only if that report still matches this test.
  const labReportId =
    getBoundPathLabReportId(scopedLabTest) ?? getBoundPathLabReportId(labTest);
  if (labReportId != null) {
    const fromLab = accept(findByReportId(labReportId));
    if (fromLab != null) return fromLab;
  }

  // 3) Paired report from entry resolution (must match — never trust a mismatched id).
  const fromPaired = accept(report);
  if (fromPaired != null) return fromPaired;

  // 4) Pool lookup scoped by patient + catalog/name.
  const poolReport = findReportForOpdLabTest(
    reportList,
    prescription,
    scopedLabTest,
    catalogByName,
    catalogById,
    preferredReportId
  );
  return accept(poolReport);
};

/** Unique form state key — prefer reportId so same test on different dates do not share form state. */
export const getOpdTestFormKey = (labTest, report, catalogByName = null, catalogById = null) => {
  const reportId = getPathLabReportId(report) ?? getBoundPathLabReportId(labTest);
  if (reportId != null) return `report-${reportId}`;

  const catalogId = resolveAuthoritativeCatalogId(labTest, report, catalogByName, catalogById);
  if (catalogId != null) return `test-${catalogId}`;

  return `name-${normalizeTestName(getLabTestName(labTest))}`;
};

/** Pair prescription lab test with report; prescription catalog id is authoritative. */
export const resolveOpdTestEntry = (labTest, report, catalogById, catalogByName) => {
  const resolvedFromPrescription = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  const catalogId = resolveAuthoritativeCatalogId(
    resolvedFromPrescription,
    report,
    catalogByName,
    catalogById
  );
  const testName = resolveCanonicalTestNameForEntry(
    resolvedFromPrescription,
    report,
    catalogById,
    catalogByName
  );

  const mergedLabTest = {
    ...resolvedFromPrescription,
    testName: testName || getLabTestName(resolvedFromPrescription),
    labTestId: catalogId ?? resolvedFromPrescription.labTestId ?? null,
    testId: catalogId ?? resolvedFromPrescription.testId ?? null,
  };

  return {
    labTest: mergedLabTest,
    report,
    formKey: getOpdTestFormKey(mergedLabTest, report, catalogByName, catalogById),
    catalogId,
  };
};

const findReportByIdForLabTest = (reports, labTest) => {
  const labTestReportId = getBoundPathLabReportId(labTest);
  if (labTestReportId == null) return null;

  const found =
    (Array.isArray(reports) ? reports : []).find((report) => {
      return getPathLabReportId(report) === labTestReportId;
    }) || null;

  if (!found) return null;
  return reportMatchesLabTest(found, labTest) ? found : null;
};

const prescriptionIdsMatch = (left, right) => {
  if (left == null || right == null) return false;
  if (left === right) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
    return leftNum === rightNum;
  }
  return false;
};

const buildReportFromLabTest = (labTest, prescription) => {
  const labCatalogId = getLabTestCatalogId(labTest);
  const resolvedReportDate =
    labTest?.reportDate ||
    labTest?.reportData?.reportDate ||
    labTest?.reportData?.createdAt ||
    prescription?.createdAt ||
    null;
  const resolvedTestName = getLabTestName(labTest);

  if (labTest?.reportData) {
    const reportId =
      getPathLabReportId(labTest.reportData) ??
      getBoundPathLabReportId(labTest);
    const reportCatalogId = getLabTestCatalogId(labTest.reportData);
    const catalogMatches =
      labCatalogId == null ||
      reportCatalogId == null ||
      labCatalogId === reportCatalogId;
    if (!catalogMatches) return null;

    const reportResult = getPathLabReportResultValue(labTest.reportData);

    return {
      ...labTest.reportData,
      reportId,
      id: reportId,
      testId: labCatalogId ?? reportCatalogId ?? labTest.reportData.testId,
      labTestId: labCatalogId ?? reportCatalogId ?? labTest.reportData.labTestId,
      testName:
        resolvedTestName ||
        labTest.reportData.testName ||
        labTest.reportData.name,
      reportDate: resolvedReportDate,
      result: reportResult ?? getPathLabReportResultValue(labTest),
      prescriptionId:
        labTest.reportData.prescriptionId ??
        labTest.reportData.prescription_id ??
        labTest.prescriptionId ??
        prescription?.id ??
        null,
      patientId:
        labTest.reportData.patientId ??
        labTest.reportData.patient_id ??
        labTest.patientId ??
        prescription?.patientId ??
        null,
      appointmentId:
        labTest.reportData.appointmentId ??
        labTest.reportData.appointment_id ??
        labTest.appointmentId ??
        getLinkedAppointmentId(prescription) ??
        null,
    };
  }

  const reportId = getBoundPathLabReportId(labTest);
  if (reportId == null) return null;

  // Do not synthesize report rows from bare reportId on prescription lab tests;
  // stale ids (e.g. Typhoid pointing at Urine report 649) corrupt bulk submit.
  return null;
};

/** Path lab reports embedded on /withlabtest prescription rows. */
export const getPrescriptionPathLabReports = (prescription) => {
  const reports = prescription?.pathLabReports;
  return Array.isArray(reports) ? reports : [];
};

const pickLatestReport = (candidateReports) => {
  if (!candidateReports.length) return null;

  return candidateReports.reduce((latest, current) => {
    if (!latest) return current;
    const currentDate = current.reportDate || current.createdAt || current.date;
    const latestDate = latest.reportDate || latest.createdAt || latest.date;
    if (!currentDate) return latest;
    if (!latestDate) return current;
    return dayjs(currentDate).isAfter(dayjs(latestDate)) ? current : latest;
  }, null);
};

const filterReportsByPrescriptionDate = (candidateReports, prescription) => {
  const prescDate = prescription?.createdAt ? dayjs(prescription.createdAt) : null;
  if (!prescDate || candidateReports.length <= 1) return candidateReports;

  const dateFiltered = candidateReports.filter((report) => {
    const reportDate = report.reportDate || report.createdAt || report.date;
    return reportDate ? dayjs(reportDate).isSame(prescDate, "day") : true;
  });

  return dateFiltered.length > 0 ? dateFiltered : candidateReports;
};

const getReportsForPrescriptionPool = (prescription, reportPool = []) => {
  const embedded = getPrescriptionPathLabReports(prescription);
  const prescId = prescription?.id;
  const patientId = prescription?.patientId;
  const isVirtualPrescId = typeof prescId === "string" && prescId.startsWith("virtual_");

  const fromPool = (Array.isArray(reportPool) ? reportPool : []).filter((report) => {
    const reportPrescId = report.prescriptionId ?? report.prescription_id;
    if (prescId && !isVirtualPrescId && prescriptionIdsMatch(reportPrescId, prescId)) {
      return true;
    }
    if (patientId == null) return false;
    const reportPatientId = report.patientId ?? report.patient_id;
    return reportPatientId != null && Number(reportPatientId) === Number(patientId);
  });

  return dedupePathLabReportRecords([...embedded, ...fromPool]);
};

export const findReportForOpdLabTest = (
  reports,
  prescription,
  labTest,
  catalogByName = null,
  catalogById = null,
  preferredReportId = null
) => {
  const resolvedLabTest = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  const patientId =
    prescription?.patientId ??
    resolvedLabTest?.patientId ??
    labTest?.patientId ??
    null;
  const scopedLabTest = {
    ...resolvedLabTest,
    patientId: patientId ?? resolvedLabTest?.patientId ?? null,
  };

  // Always scope by patient id first — never match another patient's report by name.
  const reportList = (Array.isArray(reports) ? reports : []).filter((report) => {
    if (patientId == null) return true;
    const reportPatientId = report?.patientId ?? report?.patient_id;
    if (reportPatientId == null) return true;
    return Number(reportPatientId) === Number(patientId);
  });

  const labCatalogId = getLabTestCatalogId(scopedLabTest);
  const labTestReportId = getBoundPathLabReportId(scopedLabTest);
  const testNameNormalized = normalizeTestName(getLabTestName(scopedLabTest));
  const prescId = prescription?.id;
  const isVirtualPrescId = typeof prescId === "string" && prescId.startsWith("virtual_");

  // Table-row edit: bind the exact reportId only when identity still matches this lab test.
  if (preferredReportId != null && preferredReportId !== "") {
    const preferred =
      reportList.find(
        (report) => Number(getPathLabReportId(report)) === Number(preferredReportId)
      ) || null;
    if (preferred) {
      if (reportMatchesLabTest(preferred, scopedLabTest)) return preferred;
      const preferredName = getReportTestName(preferred);
      // Require name agreement when both sides have names — never accept a blank-name
      // lab test as a match for any preferred report (cross-test overwrite root cause).
      if (testNameNormalized && preferredName && testNameNormalized === preferredName) {
        return preferred;
      }
    }
  }

  const matchesCatalogId = (report) => {
    if (labCatalogId == null || getLabTestCatalogId(report) !== labCatalogId) {
      return false;
    }
    const reportName = getReportTestName(report);
    if (testNameNormalized && reportName && testNameNormalized !== reportName) {
      return false;
    }
    return true;
  };

  const matchesTestName = (report) => {
    if (!testNameNormalized) return false;
    return getReportTestName(report) === testNameNormalized;
  };

  const filterByPatient = (candidates) => {
    if (patientId == null) return candidates;
    return candidates.filter((report) => {
      const reportPatientId = report.patientId || report.patient_id;
      if (reportPatientId == null) return true;
      return Number(reportPatientId) === Number(patientId);
    });
  };

  const filterByPrescription = (candidates) => {
    if (!prescId || isVirtualPrescId) return candidates;
    const byPresc = candidates.filter((report) => {
      const reportPrescId = report.prescriptionId || report.prescription_id;
      return reportPrescId != null && prescriptionIdsMatch(reportPrescId, prescId);
    });
    // When API rows lack prescriptionId, keep date/patient-filtered candidates only.
    return byPresc.length > 0 ? byPresc : candidates;
  };

  const pickBest = (candidates) => {
    let filtered = filterReportsByPrescriptionDate(candidates, prescription);
    filtered = filterByPrescription(filtered);
    filtered = filterByPatient(filtered);
    return pickLatestReport(filtered);
  };

  if (labTestReportId != null) {
    const byReportId = findReportByIdForLabTest(reportList, scopedLabTest);
    if (byReportId) return byReportId;
  }

  if (labCatalogId != null) {
    const picked = pickBest(reportList.filter(matchesCatalogId));
    if (picked) return picked;
  }

  if (prescId && !isVirtualPrescId && testNameNormalized) {
    const picked = pickBest(
      reportList.filter(
        (report) =>
          matchesTestName(report) &&
          prescriptionIdsMatch(report.prescriptionId || report.prescription_id, prescId)
      )
    );
    if (picked) return picked;
  }

  if (testNameNormalized) {
    const picked = pickBest(reportList.filter(matchesTestName));
    if (picked) return picked;
  }

  return null;
};

export const findExistingLabTestForReport = (patientLabTests, report) => {
  const reportCatalogId = getLabTestCatalogId(report);
  const reportId = getPathLabReportId(report);
  const reportName = getReportTestName(report);
  const reportPatientId = report?.patientId ?? report?.patient_id ?? null;
  const reportDateKey = resolvePathLabReportDateKey(report);

  return (patientLabTests || []).find((test) => {
    const testPatientId = test?.patientId ?? test?.patient_id ?? null;
    if (
      reportPatientId != null &&
      testPatientId != null &&
      Number(reportPatientId) !== Number(testPatientId)
    ) {
      return false;
    }

    const testReportId = getBoundPathLabReportId(test);
    const testCatalogId = getLabTestCatalogId(test);
    const testName = normalizeTestName(getLabTestName(test));

    // Exact reportId bind — strongest match.
    if (reportId != null && testReportId != null && reportId === testReportId) {
      if (
        reportCatalogId != null &&
        testCatalogId != null &&
        reportCatalogId !== testCatalogId
      ) {
        return false;
      }
      if (reportName && testName && reportName !== testName) return false;
      return true;
    }

    // Already bound to a different report — never reuse this lab-test slot.
    if (testReportId != null && reportId != null && testReportId !== reportId) {
      return false;
    }

    // Catalog match only when names agree (or one side lacks a name) AND the lab test
    // is unbound or same-day — prevents Typhoid "latest" from overwriting Urine.
    if (reportCatalogId != null && testCatalogId === reportCatalogId) {
      if (reportName && testName && reportName !== testName) return false;
      if (testReportId == null) return true;
      if (reportDateKey) {
        const testDateKey = resolvePathLabReportDateKey(test);
        if (testDateKey && testDateKey === reportDateKey) return true;
      }
      return false;
    }

    if (!reportName) return false;
    if (!(testName && testName === reportName)) return false;
    // Name-only: only bind unbound lab tests (never steal another report's slot).
    return testReportId == null;
  });
};

export const applyPathReportToLabTest = (
  labTest,
  report,
  catalogById = null,
  catalogByName = null
) => {
  if (!labTest || !report) return labTest;

  const resolvedLabTest = resolveLabTestIdentity(labTest, catalogByName, catalogById);
  const labHasIdentity =
    Boolean(getLabTestName(resolvedLabTest)) || getLabTestCatalogId(resolvedLabTest) != null;
  if (labHasIdentity && !reportMatchesLabTest(report, resolvedLabTest)) {
    return labTest;
  }

  const reportCatalogId = getLabTestCatalogId(report);
  // Prefer the report's own API name so catalog remaps cannot rename CBC → Thyroid.
  const apiName = getLabTestName(report);
  const resolvedName =
    apiName ||
    resolveReportTestNameFromCatalog(report, catalogById, catalogByName) ||
    getLabTestName(resolvedLabTest) ||
    labTest.testName;

  return {
    ...resolvedLabTest,
    __entity: "patientLabTest",
    testName: resolvedName,
    labTestId:
      getLabTestCatalogId(resolvedLabTest) ??
      reportCatalogId ??
      labTest.labTestId ??
      labTest.testId ??
      null,
    testId:
      getLabTestCatalogId(resolvedLabTest) ??
      reportCatalogId ??
      labTest.testId ??
      labTest.labTestId ??
      null,
    reportId: getPathLabReportId(report) ?? getBoundPathLabReportId(labTest) ?? null,
    result: getPathLabReportResultValue(report) ?? labTest.result ?? null,
    resultStatus:
      report.resultStatus ?? report.result_status ?? labTest.resultStatus ?? null,
    normalRange: report.normalRange ?? labTest.normalRange ?? null,
    reportDate: report.reportDate ?? report.createdAt ?? labTest.reportDate ?? null,
    patientId:
      resolvedLabTest.patientId ??
      labTest.patientId ??
      report.patientId ??
      report.patient_id ??
      null,
  };
};

/** Flatten embedded reports from a prescription list (replaces getAllReports on queue screens). */
export const aggregatePathLabReportsFromPrescriptions = (prescriptions = []) => {
  const all = [];
  const seenReportIds = new Set();

  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    getPrescriptionPathLabReports(prescription).forEach((report) => {
      const reportId = report?.reportId ?? report?.id;
      if (reportId != null) {
        if (seenReportIds.has(reportId)) return;
        seenReportIds.add(reportId);
      }
      all.push({
        ...report,
        prescriptionId:
          report.prescriptionId ?? report.prescription_id ?? prescription?.id ?? null,
        patientId: report.patientId ?? report.patient_id ?? prescription?.patientId ?? null,
        patientName: report.patientName ?? prescription?.patientName ?? null,
        doctorId: report.doctorId ?? prescription?.doctorId ?? null,
        doctorName: report.doctorName ?? prescription?.doctorName ?? null,
      });
    });
  });

  return dedupePathLabReportRecords(all);
};

/** Merge embedded pathLabReports onto patientLabTests for OPD workflow cards. */
export const enrichOpdPrescriptionWithReports = (
  prescription,
  reportPool = null,
  catalogByName = null,
  catalogById = null
) => {
  if (!prescription) return prescription;

  const reports = reportPool
    ? getReportsForPrescriptionPool(prescription, reportPool)
    : getPrescriptionPathLabReports(prescription);
  const patientLabTests = (prescription.patientLabTests || []).map((labTest) => {
    const resolvedLabTest = resolveLabTestIdentity(labTest, catalogByName, catalogById);
    const reportId = getBoundPathLabReportId(resolvedLabTest);
    const labCatalogId = getLabTestCatalogId(resolvedLabTest);

    let matchingReport = null;
    if (reportId != null) {
      const byId =
        reports.find((report) => getPathLabReportId(report) === reportId) || null;
      if (byId && reportMatchesLabTest(byId, resolvedLabTest)) {
        matchingReport = byId;
      }
    }
    if (!matchingReport) {
      matchingReport = findReportForOpdLabTest(
        reports,
        prescription,
        resolvedLabTest,
        catalogByName,
        catalogById
      );
    }
    const reportIsMatch = matchingReport && reportMatchesLabTest(matchingReport, resolvedLabTest);
    const reportCatalogId = getLabTestCatalogId(matchingReport);
    const reportApiName =
      reportIsMatch && matchingReport ? getLabTestName(matchingReport) : "";
    const resolvedTestName =
      reportIsMatch && matchingReport
        ? reportApiName ||
          getLabTestName(resolvedLabTest) ||
          resolveReportTestNameFromCatalog(matchingReport, catalogById, catalogByName)
        : getLabTestName(resolvedLabTest) || labTest.testName;
    // Prefer prescription catalog id; only take report catalog id when names agree.
    const resolvedCatalogId =
      labCatalogId ??
      (reportIsMatch &&
      reportCatalogId != null &&
      (!reportApiName ||
        !getLabTestName(resolvedLabTest) ||
        normalizeTestName(reportApiName) ===
          normalizeTestName(getLabTestName(resolvedLabTest)))
        ? reportCatalogId
        : null);

    // Never stamp another test's reportId/result onto this lab test when identity disagrees.
    const boundReportId = reportIsMatch ? getPathLabReportId(matchingReport) ?? null : null;
    // Keep previously bound reportId only if we could not re-resolve a safe match.
    const nextReportId =
      boundReportId ??
      (reportId != null && !matchingReport ? reportId : null);

    return {
      ...resolvedLabTest,
      __entity: "patientLabTest",
      testName: resolvedTestName,
      prescriptionId: prescription.id,
      patientId: prescription.patientId,
      patientName: prescription.patientName,
      doctorId: prescription.doctorId,
      doctorName: prescription.doctorName,
      createdAt: prescription.createdAt,
      reportId: nextReportId,
      reportData: reportIsMatch ? matchingReport : labTest.reportData || null,
      result: reportIsMatch
        ? getPathLabReportResultValue(matchingReport)
        : getPathLabReportResultValue(labTest) ?? null,
      normalRange: reportIsMatch
        ? matchingReport?.normalRange ?? labTest.normalRange ?? null
        : labTest.normalRange ?? null,
      reportDate:
        (reportIsMatch
          ? matchingReport?.reportDate ?? matchingReport?.createdAt
          : null) ??
        labTest.reportDate ??
        prescription?.createdAt ??
        null,
      resultStatus: reportIsMatch
        ? matchingReport?.resultStatus ??
          matchingReport?.result_status ??
          labTest.resultStatus ??
          null
        : labTest.resultStatus ?? null,
      labTestId: resolvedCatalogId ?? resolvedLabTest.labTestId ?? null,
      testId: resolvedCatalogId ?? resolvedLabTest.testId ?? null,
    };
  });

  return {
    ...prescription,
    patientLabTests,
    pathLabReports: reports,
  };
};

/** Collect embedded reports from pathLabReports and patientLabTests.reportId fields. */
export const buildReportsFromPrescription = (prescription) => {
  // pathLabReports rows don't carry their own appointmentId — backfill it from
  // the prescription they're embedded on (same resolution buildReportFromLabTest
  // uses below) so the timeline/print heading can show it.
  const fromPathLabReports = getPrescriptionPathLabReports(prescription).map((report) => ({
    ...report,
    appointmentId:
      report?.appointmentId ??
      report?.appointment_id ??
      getLinkedAppointmentId(prescription) ??
      null,
  }));
  const fromLabTests = (prescription?.patientLabTests || [])
    .filter((labTest) => {
      if (!labTest?.reportData) return false;
      const labName = normalizeTestName(getLabTestName(labTest));
      const reportName = normalizeTestName(getLabTestName(labTest.reportData));
      if (labName && reportName) return labName === reportName;
      return reportMatchesLabTest(labTest.reportData, labTest);
    })
    .map((labTest) => buildReportFromLabTest(labTest, prescription))
    .filter(Boolean);

  return dedupePathLabReportRecords([...fromPathLabReports, ...fromLabTests]);
};

/** Prefer fresh patient/API reports; use embedded as fallback only. */
export const resolvePrescriptionReports = async (prescription) => {
  const embedded = buildReportsFromPrescription(prescription);
  if (!prescription?.patientId) return embedded;

  try {
    const fromApis = await collectReportsFromPrescriptions([], prescription.patientId);
    if (!fromApis.length) return embedded;
    // Merge with patient/API last so freshly saved results win over stale embedded rows.
    return dedupePathLabReportRecords([...embedded, ...fromApis]);
  } catch (error) {
    console.warn("[labTestWorkflowUtils] resolvePrescriptionReports API refresh failed:", error);
    return embedded;
  }
};

const matchesPatientId = (prescription, patientId) => {
  const pid = prescription?.patientId ?? prescription?.patient_id;
  return pid != null && Number(pid) === Number(patientId);
};

const collectReportsFromPrescriptions = async (prescriptions, patientId) => {
  const pools = [];
  const initialList = Array.isArray(prescriptions) ? prescriptions : [];

  const addFromList = (prescList) => {
    (Array.isArray(prescList) ? prescList : [])
      .filter((prescription) => !patientId || matchesPatientId(prescription, patientId))
      .forEach((prescription) => {
        const enriched = enrichOpdPrescriptionWithReports(prescription);
        pools.push(...buildReportsFromPrescription(enriched));
      });
  };

  addFromList(initialList);

  if (patientId == null || patientId === "") {
    if (!pools.length) {
      try {
        const withLabTest = await getPrescriptionsWithLabTest();
        addFromList(withLabTest);
      } catch (error) {
        console.warn("[labTestWorkflowUtils] withlabtest load failed:", error);
      }
    }

    if (!pools.length) {
      try {
        const opdLabTests = await getOpdLabTests();
        addFromList(opdLabTests);
      } catch (error) {
        console.warn("[labTestWorkflowUtils] getOpdLabTests load failed:", error);
      }
    }

    return dedupePathLabReportRecords(pools);
  }

  // Patient-scoped load: parallel prescription + direct path-lab API (no org-wide fallbacks).
  if (!initialList.length) {
    const [byPatient, directReports] = await Promise.all([
      getPrescriptionsByPatientId(patientId).catch((error) => {
        console.warn("[labTestWorkflowUtils] Prescription-by-patient load failed:", error);
        return [];
      }),
      getPathPabReportByPatient(patientId).catch((error) => {
        console.warn("[labTestWorkflowUtils] Path-lab reports-by-patient load failed:", error);
        return [];
      }),
    ]);

    addFromList(byPatient);

    const directNormalized = (Array.isArray(directReports) ? directReports : []).map((report) =>
      normalizePathLabReportFields({
        ...report,
        patientId: report?.patientId ?? report?.patient_id ?? patientId,
      })
    );

    const directReportIds = new Set(
      directNormalized
        .map((row) => getPathLabReportId(row))
        .filter((id) => id != null)
    );

    const prescDerived = pools.filter((row) => {
      const reportId = getPathLabReportId(row);
      return reportId == null || !directReportIds.has(reportId);
    });

    // Patient API rows are authoritative; prescription-derived duplicates must not override them.
    return dedupePathLabReportRecords([...directNormalized, ...prescDerived]);
  }

  return dedupePathLabReportRecords(pools);
};

/** Build per-patient report index from IPD lab test rows (no /pathlabreports API). */
export const buildIpdReportsByPatientFromRows = (rows = []) => {
  const byPatient = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const patientId = row?.patientId;
    if (patientId == null || patientId === "") return;

    const reportId = row.reportId ?? row.report_id ?? row.pathLabReportId ?? null;
    const testName =
      row.resolvedTestName ||
      row.testName ||
      row.test_name ||
      row.labTestName ||
      row.name ||
      "";

    if (!reportId && !testName) return;

    if (!byPatient[patientId]) byPatient[patientId] = [];
    byPatient[patientId].push({
      reportId,
      testId: row.testId ?? row.labTestId ?? null,
      testName,
      result: row.result ?? null,
      resultStatus: row.resultStatus ?? row.status ?? null,
      patientId,
      patientName: row.patientName ?? null,
      reportDate: row.reportDate ?? row.testDate ?? null,
      normalRange: row.normalRange ?? null,
    });
  });
  return byPatient;
};

const normalizePathLabReportRow = (report, catalogById, catalogByName) => {
  const catalogId = getLabTestCatalogId(report);
  const apiTestName = getLabTestName(report) || report?.testName || report?.test_name || "";
  // Prefer the API/report name when present so catalog remaps can't hide real tests (e.g. Urine).
  const resolvedName = apiTestName
    ? apiTestName
    : resolveReportTestNameFromCatalog(report, catalogById, catalogByName);

  const catalogTest = resolveCatalogTestForReport(
    { ...report, testName: resolvedName || apiTestName },
    catalogById,
    catalogByName
  );
  // Prefer the report's own embedded subCategories (now returned directly by
  // GET /pathlabreports/patient/{id}) over the catalog definition — same
  // "live API data wins" priority as testName above. Catalog stays as the
  // fallback for any row that still arrives without them.
  const reportSubCategories = normalizeCatalogSubCategories(report);
  const catalogSubCategories = normalizeCatalogSubCategories(catalogTest);
  const subCategories =
    reportSubCategories.length > 0 ? reportSubCategories : catalogSubCategories;
  const refMeta = resolveLabTestReferenceMeta({
    testName: resolvedName || apiTestName,
    catalogTest,
    report,
    subCategories,
  });

  return {
    ...report,
    testId: catalogId ?? report?.testId ?? report?.labTestId ?? null,
    labTestId: catalogId ?? report?.labTestId ?? report?.testId ?? null,
    testName: resolvedName || "—",
    reportDate: resolvePathLabReportDate(report) || report.reportDate || null,
    // Org/custom lab-test definition (used by PathLabTest report view for Ref. Value)
    subCategories,
    normalRange: refMeta.referenceValue || report?.normalRange || report?.normal_range || null,
    referenceValue: refMeta.referenceValue || report?.referenceValue || null,
    unit: refMeta.unit || report?.unit || "",
  };
};

/**
 * Load path lab reports for a patient.
 * Default: patient API + prescriptions (fast). Pass mergeOrgLite=true to also merge
 * completed/billed (restores tests omitted by patient API, e.g. Urine 937).
 */
export const getPathLabReportsForPatient = async (
  patientId,
  { mergeOrgLite = false, prescriptions: prefetchedPrescriptions = null } = {}
) => {
  if (patientId == null || patientId === "") return [];

  const loadOrgLite = mergeOrgLite
    ? getMergedPathLabReportsForPatient(patientId).catch((error) => {
        console.warn("[labTestWorkflowUtils] Merged patient reports load failed:", error);
        return getPathPabReportByPatient(patientId).catch(() => []);
      })
    : getPathPabReportByPatient(patientId).catch((error) => {
        console.warn("[labTestWorkflowUtils] Path-lab reports-by-patient load failed:", error);
        return [];
      });

  // Callers that already fetched prescriptions for this patient (e.g. the Patient
  // Timeline, which loads the Prescriptions tab in the same pass) can pass them in
  // here to avoid a second /prescriptions/patient/{id} round trip.
  const prescriptionsPromise = Array.isArray(prefetchedPrescriptions)
    ? Promise.resolve(prefetchedPrescriptions)
    : getPrescriptionsByPatientId(patientId).catch((error) => {
        console.warn("[labTestWorkflowUtils] Prescription-by-patient load failed:", error);
        return [];
      });

  const [{ catalogById, catalogByName }, mergedDirect, prescriptions] = await Promise.all([
    getLabTestCatalogMaps(),
    loadOrgLite,
    prescriptionsPromise,
  ]);

  const byReportId = new Map();
  const upsert = (row) => {
    const normalized = normalizePathLabReportFields({
      ...row,
      patientId: row?.patientId ?? row?.patient_id ?? patientId,
    });
    const reportId = getPathLabReportId(normalized);
    if (reportId == null) {
      byReportId.set(`noid:${byReportId.size}:${getLabTestName(normalized)}`, normalized);
      return;
    }
    const existing = byReportId.get(reportId);
    byReportId.set(
      reportId,
      existing ? mergePathLabReportPreferFilled(existing, normalized) : normalized
    );
  };

  (Array.isArray(prescriptions) ? prescriptions : [])
    .filter((prescription) => matchesPatientId(prescription, patientId))
    .forEach((prescription) => {
      const enriched = enrichOpdPrescriptionWithReports(prescription);
      buildReportsFromPrescription(enriched).forEach(upsert);
    });

  // Patient/org API rows last so saved COMPLETE results are not overwritten by stale embedded prescription data.
  (Array.isArray(mergedDirect) ? mergedDirect : []).forEach(upsert);

  // GET /pathlabreports/patient/{id} now embeds subCategories directly on each
  // report, so no per-test /labtests/{id} hydration pass is needed anymore.
  const reports = Array.from(byReportId.values())
    .filter((report) => {
      const reportPatientId = report?.patientId ?? report?.patient_id;
      if (reportPatientId == null) return true;
      return Number(reportPatientId) === Number(patientId);
    })
    .map((report) => normalizePathLabReportRow(report, catalogById, catalogByName));

  // Reports sourced straight from the pathlabreports API (not embedded on a
  // prescription) never carry appointmentId — backfill it here from the
  // patient's own prescriptions via prescriptionId, so every report gets it
  // regardless of which merge path it came through.
  const appointmentIdByPrescriptionId = new Map();
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    const pid = getPrescriptionRecordId(prescription);
    const aptId = getLinkedAppointmentId(prescription);
    if (pid != null && aptId != null && !appointmentIdByPrescriptionId.has(String(pid))) {
      appointmentIdByPrescriptionId.set(String(pid), aptId);
    }
  });
  const reportsWithAppointmentIds = reports.map((report) => {
    if (report.appointmentId != null) return report;
    const prescriptionId = report.prescriptionId ?? report.prescription_id;
    if (prescriptionId == null) return report;
    const resolved = appointmentIdByPrescriptionId.get(String(prescriptionId));
    return resolved != null ? { ...report, appointmentId: resolved } : report;
  });

  return reportsWithAppointmentIds.sort(
    (a, b) => Number(b.reportId ?? b.id ?? 0) - Number(a.reportId ?? a.id ?? 0)
  );
};

/** Flat OPD queue rows — one row per path-lab report (OpdIpdPatients table). */
export const isUsableOpdDoctorName = (name) => {
  if (name == null) return false;
  const text = String(name).trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower !== "n/a" &&
    lower !== "null" &&
    lower !== "—" &&
    lower !== "-" &&
    lower !== "unknown doctor" &&
    lower !== "unknown" &&
    !lower.startsWith("dr. id:")
  );
};

export const mergeCompletedDoctorNamesOntoRows = (rows, doctorByReportId) => {
  if (!(doctorByReportId instanceof Map) || doctorByReportId.size === 0) {
    return rows;
  }
  return (Array.isArray(rows) ? rows : []).map((row) => {
    if (isUsableOpdDoctorName(row.doctorName)) return row;
    const reportId = row.reportId ?? row.id;
    if (reportId == null) return row;
    const fromCompleted = doctorByReportId.get(Number(reportId));
    return fromCompleted ? { ...row, doctorName: fromCompleted } : row;
  });
};

export const enrichOpdTableRowsDoctorNames = (
  rows = [],
  { prescriptions = [], doctorByPatientId = null, doctorIdNameMap = null } = {}
) => {
  const byPatient =
    doctorByPatientId instanceof Map
      ? doctorByPatientId
      : new Map(
          Object.entries(doctorByPatientId || {}).map(([key, value]) => [Number(key), value])
        );
  const byPrescription = new Map();

  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    if (!isUsableOpdDoctorName(prescription?.doctorName)) return;
    const doctorName = String(prescription.doctorName).trim();
    if (prescription.id != null) {
      byPrescription.set(prescription.id, doctorName);
      byPrescription.set(String(prescription.id), doctorName);
      const numericId = Number(prescription.id);
      if (!Number.isNaN(numericId)) byPrescription.set(numericId, doctorName);
    }
    const patientId = Number(prescription.patientId ?? prescription.patient_id);
    if (!Number.isNaN(patientId) && patientId > 0 && !byPatient.has(patientId)) {
      byPatient.set(patientId, doctorName);
    }
  });

  return (Array.isArray(rows) ? rows : []).map((row) => {
    if (isUsableOpdDoctorName(row.doctorName)) return row;

    const prescId = row.prescriptionId ?? row.prescription_id ?? null;
    const patientId = Number(row.patientId ?? row.patient_id);
    const doctorId = Number(row.doctorId ?? row.assignedBy);

    const resolved =
      (prescId != null
        ? byPrescription.get(prescId) ??
          byPrescription.get(String(prescId)) ??
          byPrescription.get(Number(prescId))
        : null) ||
      (!Number.isNaN(patientId) && patientId > 0 ? byPatient.get(patientId) : null) ||
      (!Number.isNaN(doctorId) && doctorId > 0 && doctorIdNameMap instanceof Map
        ? doctorIdNameMap.get(doctorId)
        : null) ||
      (isUsableOpdDoctorName(row.assignedByName) ? String(row.assignedByName).trim() : null);

    return resolved ? { ...row, doctorName: resolved } : row;
  });
};

export const buildOpdTableRowsFromReports = (
  reports = [],
  prescriptions = [],
  catalogById = null,
  catalogByName = null,
  patientNameById = null
) => {
  const nameByPatientId =
    patientNameById instanceof Map
      ? patientNameById
      : new Map(
          Object.entries(patientNameById || {}).map(([key, value]) => [Number(key), value])
        );

  const prescById = new Map();
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    if (prescription?.id == null) return;
    prescById.set(prescription.id, prescription);
    prescById.set(String(prescription.id), prescription);
    const numericId = Number(prescription.id);
    if (!Number.isNaN(numericId)) prescById.set(numericId, prescription);
  });

  const deduped = dedupePathLabReportRecords(
    (Array.isArray(reports) ? reports : []).map(normalizePathLabReportFields)
  );

  return deduped.map((report) => {
    const normalized = normalizePathLabReportRow(report, catalogById, catalogByName);
    const prescId = normalized.prescriptionId ?? normalized.prescription_id ?? null;
    const presc =
      prescId != null
        ? prescById.get(prescId) ??
          prescById.get(Number(prescId)) ??
          prescById.get(String(prescId)) ??
          null
        : null;
    const patientId =
      normalized.patientId ??
      normalized.patient_id ??
      presc?.patientId ??
      presc?.patient_id ??
      null;

    const doctorName =
      (normalized.doctorName && String(normalized.doctorName).trim() !== "—"
        ? normalized.doctorName
        : null) ||
      presc?.doctorName ||
      normalized.assignedByName ||
      null;

    return {
      ...normalized,
      reportId: getPathLabReportId(normalized) ?? normalized.reportId ?? normalized.id ?? null,
      id: getPathLabReportId(normalized) ?? normalized.id ?? null,
      prescriptionId: prescId ?? presc?.id ?? null,
      patientId,
      patientName:
        normalized.patientName ??
        presc?.patientName ??
        (patientId != null ? nameByPatientId.get(Number(patientId)) : null) ??
        "—",
      doctorId: normalized.doctorId ?? presc?.doctorId ?? null,
      doctorName: doctorName || "—",
      result: getPathLabReportResultValue(normalized),
      resultStatus:
        normalized.resultStatus ??
        normalized.result_status ??
        normalized.status ??
        "PENDING",
      reportDate:
        resolvePathLabReportDate(normalized) ??
        normalized.reportDate ??
        presc?.createdAt ??
        null,
      createdAt:
        normalized.createdAt ??
        presc?.createdAt ??
        resolvePathLabReportDate(normalized) ??
        null,
    };
  });
};

export const groupIpdTests = (tests) => {
  const groups = new Map();

  (tests || []).forEach((test) => {
    const patientId = test?.patientId ?? "unknown";
    const admissionId = test?.admissionId ?? "unknown";
    const testDateValue = test?.testDate || test?.reportDate || "";
    const dateKey = Array.isArray(testDateValue)
      ? testDateValue.slice(0, 3).join("-")
      : testDateValue
        ? dayjs(testDateValue).format("YYYY-MM-DD")
        : "unknown-date";
    const groupKey = `${patientId}|${admissionId}|${dateKey}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        patientId: test.patientId,
        patientName: test.patientName,
        admissionId: test.admissionId,
        testDate: test.testDate,
        createdAt: test.createdAt ?? test.created_at ?? null,
        appointmentId: test.appointmentId ?? test.appointment_id ?? null,
        prescriptionId:
          test.prescriptionId ??
          test.prescription_id ??
          test.ipdPrescriptionId ??
          test.ipd_prescription_id ??
          null,
        assignedBy: test.assignedBy,
        assignedByName: test.assignedByName || test.resolvedDoctorName || test.doctorName,
        doctorName: test.resolvedDoctorName || test.doctorName || test.assignedByName,
        labTests: [],
      });
    }

    groups.get(groupKey).labTests.push(test);

    const group = groups.get(groupKey);
    const resolvedName = test.resolvedDoctorName || test.doctorName || test.assignedByName;
    if (resolvedName && (!group.doctorName || group.doctorName === "—" || group.doctorName === "N/A")) {
      group.doctorName = resolvedName;
      group.assignedByName = resolvedName;
    }
    if (!group.appointmentId) {
      group.appointmentId = test.appointmentId ?? test.appointment_id ?? null;
    }
    if (!group.prescriptionId) {
      group.prescriptionId =
        test.prescriptionId ??
        test.prescription_id ??
        test.ipdPrescriptionId ??
        test.ipd_prescription_id ??
        null;
    }
    if (!group.createdAt) {
      group.createdAt = test.createdAt ?? test.created_at ?? null;
    }
  });

  return Array.from(groups.values());
};
